import type { CSSObject } from '@emotion/react'
import { ExpandableChevronIndicator } from 'components/ExpandableChevronIndicator/ExpandableChevronIndicator'
import { IconMapControls } from 'components/icons/IconMapControls'
import type { Dispatch, FC, SetStateAction } from 'react'
import React from 'react'
import { alphaColorReplacer, colors } from 'settings/colors'
import { useIsServiceCenterActive } from 'utils/useIsServiceCenterActive'
import { useKeyboardVisible } from 'utils/useKeyboardVisible'
import { useViewportSizeWithCorrectInitialState } from '../../utils/useViewportSizeWithCorrectInitialState'
import { NARROW_SCREEN_PANEL_Z_INDEX } from './narrowScreenPanelZIndex'

type NarrowScreenPanelProps = {
  children?: React.ReactNode
  expandedContent?: React.ReactNode
  isExpanded: boolean
  setIsExpanded: Dispatch<SetStateAction<boolean>>
  style?: React.CSSProperties | CSSObject
}

export const NarrowScreenPanel: FC<NarrowScreenPanelProps> = ({
  children,
  expandedContent,
  isExpanded,
  setIsExpanded,
  style: panelStyle,
}) => {
  const viewportSize = useViewportSizeWithCorrectInitialState()
  const { isKeyboardVisible } = useKeyboardVisible()

  const visibleHeight = isKeyboardVisible
    ? viewportSize.height
    : `calc(${viewportSize.height}px - env(safe-area-inset-bottom))`

  const isServiceCenterActive = useIsServiceCenterActive()

  const wrapperStyle: CSSObject =
    isServiceCenterActive && isExpanded
      ? {
          width: '100vw',
          transform: 'translate(-10px,75px)',
          padding: '14px 14px 20px',
        }
      : {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }

  const closeButtonStyle: CSSObject =
    isServiceCenterActive && isExpanded
      ? {
          position: 'fixed',
          width: '100vw',
          height: '40px',
          backgroundColor: colors.midnight,
          transform: 'translate(-10px, 40px)',
        }
      : {
          flex: '0 0 auto',
          width: 40,
          backgroundColor: colors.midnight,
          color: 'white',
          cursor: 'pointer',
        }

  return (
    <div
      css={{
        width: viewportSize.width,
        height: visibleHeight,
        position: 'fixed',
        top: 0,
        pointerEvents: 'none',
        zIndex: NARROW_SCREEN_PANEL_Z_INDEX,
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          height: 'fit-content',
          position: 'absolute',
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'auto',
          ...panelStyle,
        }}
      >
        <div
          css={{
            backgroundColor: alphaColorReplacer(colors.midnight, 0.65),
            minWidth: 0,
            padding: '14px 7px 14px 10px',
            ...wrapperStyle,
          }}
        >
          <div css={{ minWidth: 0 }}>
            {!isExpanded ? (
              <div
                css={{
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  fontSize: 14,
                }}
                onClick={() => setIsExpanded((s) => !s)}
              >
                <div css={{ fontSize: 15 }}>
                  <IconMapControls />
                </div>
                <div css={{ marginLeft: 12, fontSize: 14 }}>
                  <ExpandableChevronIndicator expanded={isExpanded} expandDirection="expand-rightwards" />
                </div>
              </div>
            ) : (
              <>
                {children}
                {expandedContent}
              </>
            )}
          </div>
        </div>
        {isExpanded && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              ...closeButtonStyle,
            }}
            onClick={() => setIsExpanded((s) => !s)}
          >
            <ExpandableChevronIndicator
              expanded={isExpanded}
              expandDirection={isServiceCenterActive ? 'expand-upwards' : 'expand-rightwards'}
            />
          </div>
        )}
      </div>
    </div>
  )
}
